import { t } from "@/i18n-js/instance";
import type { StoredAttachment } from "../hooks/useAttachmentsUpload";
import { useRevealingMessageStore } from "../stores/useRevealingMessageStore";
import type { ConversationChannel } from "../types/ConversationChannel";
import type { ConversationStatus } from "../types/ConversationStatus";
import {
  getLastMessage,
  isExpectingButtonClick,
} from "../utils/conversationViewHelpers";
import { CircleBotFooterMessage } from "./common/CircleBotFooterMessage";
import { ConversationClosedFooter } from "./common/ConversationClosedFooter";
import { HelpInputArea } from "./common/HelpInputArea";

interface ConversationViewFooterProps {
  attachments: StoredAttachment[];
  channel: ConversationChannel | null;
  isBotProcessing: boolean;
  onSendMessage: (text: string) => void;
  status: ConversationStatus | null;
  sunshineConversationId?: string;
  uploadAttachments: (files: File[]) => void;
}

export const ConversationViewFooter = ({
  attachments,
  channel,
  isBotProcessing,
  onSendMessage,
  status,
  sunshineConversationId,
  uploadAttachments,
}: ConversationViewFooterProps) => {
  const { visibleMessages, queuedMessages, isRevealing } =
    useRevealingMessageStore();
  if (status === "closed") {
    return (
      <ConversationClosedFooter
        channel={channel}
        sunshineConversationId={sunshineConversationId}
      />
    );
  }

  const lastMessage = getLastMessage(visibleMessages);
  const isExpectingUserButtonClick = isExpectingButtonClick(lastMessage);

  return (
    <div className="bg-primary shrink-0 rounded-b-3xl">
      <div className="mx-4 pb-3 pt-[2px]">
        <HelpInputArea
          onSubmit={onSendMessage}
          placeholder={t(
            "help_widget.conversation_page.help_input_placeholder",
          )}
          allowAttachments={channel !== "circle_bot"}
          uploadAttachments={uploadAttachments}
          attachments={attachments}
          isSubmitDisabled={
            isBotProcessing ||
            isExpectingUserButtonClick ||
            queuedMessages.length > 0 ||
            isRevealing
          }
        />
      </div>
      {channel === "circle_bot" && <CircleBotFooterMessage />}
    </div>
  );
};
